import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Box,
    Typography,
    Stack
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import SelectLoja from "../../../../../../../components/SelectsSimpleApiQuery/SelectLoja";
import { IDataLojas } from "../../../../../../../types/api/geral/pesquisar-lojas";
import { IDataRefUltimasVendas } from "../../../../../../../types/api/estoque/detalhar-referencia-ultimas-vendas";
import { formatarValorMonetario } from "../../../../../../../helpers/masks";
import moment from "moment";
import TableSkeleton from "../../../../../../../components/BasicComponents/Skeletons/TableSkeleton";

export default function DialogUltimasVendas({
    open,
    onClose,
    produtoId
}: IDialogUltimasVendasProps) {

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [lojaFiltro, setLojaFiltro] = useState<IDataLojas | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['ultimas-vendas', produtoId, page, rowsPerPage, lojaFiltro],
        queryFn: () => axiosClient.get(`/estoque/detalhar-referencia-ultimas-vendas`, {
            params: {
                produto_id: produtoId,
                page: page + 1,
                per_page: rowsPerPage,
                ...(lojaFiltro && { loja_id: lojaFiltro })
            }
        }),
        cacheTime: 0,
        enabled: open
    })

    const ultimasVendas: IDataRefUltimasVendas[] = data?.data.data || []
    const totalVendas = data?.data.total || 0
    const carregando = isLoading || !data

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleLojaFiltroChange = (loja: IDataLojas | null) => {
        setLojaFiltro(loja);
        setPage(0);
    };

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open={open}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Últimas vendas</Typography>
                    <SelectLoja
                        value={lojaFiltro}
                        onChange={handleLojaFiltroChange}
                        fullWidth={false}
                        size="small"
                        possuiBtnLimpar
                    />
                </Stack>
            </DialogTitle>

            <DialogContent>
                {
                    carregando && (
                        <Box p={2}>
                            <TableSkeleton rows={10} columns={5} />
                        </Box>
                    )
                }
                {
                    !carregando && (
                        <>
                            <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><b>Data</b></TableCell>
                                            <TableCell><b>Loja</b></TableCell>
                                            <TableCell><b>Num. documento</b></TableCell>
                                            <TableCell><b>Cliente</b></TableCell>
                                            <TableCell align="right"><b>Quantidade</b></TableCell>
                                            <TableCell align="right"><b>Valor unit.</b></TableCell>
                                            <TableCell align="right"><b>Total</b></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {ultimasVendas.map((venda, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>
                                                    {moment(venda.data_venda).format('DD/MM/YYYY')}
                                                </TableCell>
                                                <TableCell>{venda.loja_id} - {venda.nome_loja}</TableCell>
                                                <TableCell>{venda.numero_documento}</TableCell>
                                                <TableCell>{`${venda.cliente_id ? `${venda.cliente_id} - ` : ''}${venda.nome_cliente ?? '-'}`}</TableCell>
                                                <TableCell align="right">
                                                    {venda.quantidade_vendida.toLocaleString('pt-BR')}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatarValorMonetario(venda.valor_vendido)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatarValorMonetario(venda.quantidade_vendida * venda.valor_vendido)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={totalVendas}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                labelRowsPerPage="Linhas por página:"
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                                }
                            />
                        </>
                    )}
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={onClose}
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface IDialogUltimasVendasProps {
    open: boolean;
    onClose: () => void;
    produtoId: number;
}
